import React from 'react';
import { Link } from 'react-router-dom';
import {
    Container,
    Typography,
    Button,
    Box,
    useTheme,
    alpha,
} from '@mui/material';
import { keyframes } from '@mui/system';
import {
    LayoutDashboard,
    Bell,
    BotMessageSquare,
    Landmark,
} from 'lucide-react';
import StaticHeroImage from './StaticHeroImage';

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeSlideUp = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const benefits = [
    {
        icon: LayoutDashboard,
        title: 'Stocks, Crypto, Portfolios',
        description: 'All in one place. Every asset class, every tool you need.',
    },
    {
        icon: Bell,
        title: 'Get Alerted Before the Crowd',
        description: 'Real-time notifications on market moves that matter to you.',
    },
    {
        icon: BotMessageSquare,
        title: 'Ask RodeoAI Anything',
        description: 'Your 24/7 AI research agent. Ask questions, get answers in seconds.',
    },
    {
        icon: Landmark,
        title: 'Monitor Congressional and Insider Trades',
        description: 'Track what the smart money is doing before the market reacts.',
    },
];

const BenefitCard = ({ icon: Icon, title, description }) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                flex: '1 1 220px',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                gap: 1.5,
                p: { xs: 2.5, md: 3 },
                borderRadius: 3,
                background: isDark
                    ? 'linear-gradient(145deg, rgba(25, 118, 210, 0.08), rgba(25, 118, 210, 0.03))'
                    : 'linear-gradient(145deg, rgba(25, 118, 210, 0.06), rgba(25, 118, 210, 0.02))',
                border: `1px solid ${alpha(theme.palette.primary.main, isDark ? 0.15 : 0.1)}`,
                backdropFilter: 'blur(8px)',
                transition: 'transform 0.25s ease, box-shadow 0.25s ease',
                '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.12)}`,
                },
            }}
        >
            <Box
                sx={{
                    p: 1,
                    borderRadius: 2,
                    background: alpha(theme.palette.primary.main, 0.1),
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: theme.palette.primary.main,
                }}
            >
                <Icon size={20} strokeWidth={1.75} />
            </Box>
            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 700,
                    fontSize: { xs: '0.9rem', md: '0.95rem' },
                    color: isDark ? '#fff' : theme.palette.text.primary,
                    lineHeight: 1.3,
                }}
            >
                {title}
            </Typography>
            <Typography
                variant="body2"
                sx={{
                    fontSize: { xs: '0.8rem', md: '0.85rem' },
                    color: 'text.secondary',
                    lineHeight: 1.6,
                }}
            >
                {description}
            </Typography>
        </Box>
    );
};

const Hero = () => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    return (
        <Box
            sx={{
                position: 'relative',
                overflow: 'visible',
                pt: { xs: 6, md: 10 },
                pb: { xs: 8, md: 12 },
            }}
        >
            <Container maxWidth="lg">

                {/* Top text block — centered */}
                <Box
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        mb: { xs: 5, md: 8 },
                    }}
                >
                    {/* Headline */}
                    <Box
                        sx={{
                            mb: { xs: 2.5, md: 3 },
                            animation: `${fadeSlideUp} 0.7s ease both`,
                        }}
                    >
                        <Typography
                            component="h1"
                            sx={{
                                fontWeight: 800,
                                fontSize: {
                                    xs: '2.25rem',
                                    sm: '2.75rem',
                                    md: '3.25rem',
                                    lg: '3.75rem',
                                },
                                display: 'block',
                                background: 'linear-gradient(270deg, #007FFF, #66B2FF, #99CCFF, #007FFF)',
                                backgroundSize: '300% 300%',
                                animation: `${gradientShift} 6s ease infinite`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                backgroundClip: 'text',
                            }}
                        >
                            Stay one step ahead.
                        </Typography>
                        <Typography
                            component="span"
                            sx={{
                                fontWeight: 800,
                                fontSize: {
                                    xs: '2.25rem',
                                    sm: '2.75rem',
                                    md: '3.25rem',
                                    lg: '3.75rem',
                                },
                                display: 'block',
                                color: isDark ? '#fff' : theme.palette.primary.main,
                            }}
                        >
                            Every single day.
                        </Typography>
                    </Box>

                    {/* Subheadline */}
                    <Typography
                        sx={{
                            fontWeight: 400,
                            fontSize: { xs: '1rem', sm: '1.15rem', md: '1.35rem' },
                            lineHeight: 1.6,
                            color: 'text.secondary',
                            maxWidth: '750px',
                            mb: { xs: 5, md: 6 },
                            animation: `${fadeSlideUp} 0.7s ease 0.1s both`,
                        }}
                    >
                        The research platform that gives everyday investors access to the same data the smart money relies on.
                    </Typography>

                    {/* CTA Button */}
                    <Box
                        sx={{
                            animation: `${fadeSlideUp} 0.7s ease 0.2s both`,
                        }}
                    >
                        <Link to="/Signup" style={{ textDecoration: 'none' }}>
                            <Button
                                variant="contained"
                                size="large"
                                sx={{
                                    py: { xs: 1.25, md: 1.75 },
                                    px: { xs: 4, md: 6 },
                                    fontSize: { xs: '0.95rem', md: '1.1rem' },
                                    fontWeight: 600,
                                    borderRadius: 2,
                                    background: 'linear-gradient(45deg, #1976d2 30%, #1565c0 90%)',
                                    boxShadow: '0 4px 20px rgba(25,118,210,0.35)',
                                    transition: 'all 0.3s ease-in-out',
                                    whiteSpace: 'nowrap',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 24px rgba(25,118,210,0.45)',
                                    },
                                }}
                            >
                                Sign Up For Free
                            </Button>
                        </Link>
                    </Box>
                </Box>

                {/* Mockup image */}
                <Box
                    sx={{
                        width: '100%',
                        mb: { xs: 8, md: 12 },
                        animation: `${fadeSlideUp} 0.9s ease 0.3s both`,
                        lineHeight: 0,
                    }}
                >
                    <StaticHeroImage />
                </Box>

                {/* Benefit cards */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'stretch',
                        gap: { xs: 2, md: 3 },
                        justifyContent: { xs: 'flex-start', md: 'center' },
                        flexWrap: { xs: 'nowrap', md: 'wrap' },
                        overflowX: { xs: 'auto', md: 'visible' },
                        scrollSnapType: { xs: 'x mandatory', md: 'none' },
                        WebkitOverflowScrolling: 'touch',
                        pb: { xs: 1, md: 0 },
                        mx: { xs: -2, md: 0 },
                        px: { xs: 2, md: 0 },
                        '&::-webkit-scrollbar': { display: 'none' },
                        scrollbarWidth: 'none',
                        animation: `${fadeSlideUp} 0.7s ease 0.4s both`,
                    }}
                >
                    {benefits.map((benefit, index) => (
                        <Box
                            key={index}
                            sx={{
                                scrollSnapAlign: 'start',
                                flex: { xs: '0 0 80vw', md: '1 1 220px' },
                                minWidth: { xs: '80vw', md: 'unset' },
                                display: 'flex',
                            }}
                        >
                            <BenefitCard {...benefit} />
                        </Box>
                    ))}
                </Box>

                {/* Social proof */}
                <Typography
                    sx={{
                        color: 'text.secondary',
                        fontSize: { xs: '1rem', md: '1.15rem' },
                        fontWeight: 500,
                        mt: { xs: 4, md: 6 },
                        textAlign: 'center',
                    }}
                >
                    Join thousands of investors who already trust{' '}
                    <Box component="span" sx={{ color: 'primary.main', fontWeight: 700 }}>
                        Market Rodeo
                    </Box>
                </Typography>

            </Container>
        </Box>
    );
};

export default Hero;